import { WebMercatorViewport } from "@deck.gl/core"

type ZoomBucketTable = ReadonlyArray<readonly [zoomBelow: number, value: number]>

function valueForZoom(zoom: number, table: ZoomBucketTable) {
    const bucket = table.find(([zoomBelow]) => zoom < zoomBelow)
    return bucket?.[1] ?? table[table.length - 1][1]
}

export const getSimplificationTolerance = (zoom: number) =>
    valueForZoom(zoom, [
        [5, 0.05],
        [6, 0.025],
        [7, 0.012],
        [8, 0.006],
        [9, 0.003],
        [10, 0.0015],
        [11, 0.0007],
        [12, 0.0003],
        [13, 0.00015],
        [Infinity, 0],
    ])

export const getMinShapeArea = (zoom: number) =>
    valueForZoom(zoom, [
        [5, 2_200_000_000],
        [7, 470_000_000],
        [8, 200_000_000],
        [9, 95_000_000],
        [10, 18_000_000],
        [11, 1_000_000],
        [13, 100_000],
        [Infinity, 0],
    ])

// deliberately smaller so that NOTAMs don't all only appear at the highest zoom level
export const getMinNotamShapeArea = (zoom: number) =>
    valueForZoom(zoom, [
        [5, 2_000_000],
        [7, 900_000],
        [8, 400_000],
        [9, 150_000],
        [10, 50_000],
        [11, 15_000],
        [13, 5_000],
        [Infinity, 0],
    ])

export function isAreaVisible(area: number, minShapeArea: number) {
    return area >= minShapeArea
}

export function isCategoryVisible<Category extends string>(
    category: Category | undefined,
    categoriesVisible: Record<Category, boolean>
) {
    return category == null || (categoriesVisible[category] ?? true)
}

const VIEWPORT_RENDER_PADDING_RATIO = 1.25

export function getPaddedViewportBounds(
    viewport: WebMercatorViewport,
    paddingRatio: number = VIEWPORT_RENDER_PADDING_RATIO
) {
    const [west, south, east, north] = viewport.getBounds()
    const lngPad = (east - west) * paddingRatio
    const latPad = (north - south) * paddingRatio
    return {
        minLng: west - lngPad,
        maxLng: east + lngPad,
        minLat: south - latPad,
        maxLat: north + latPad,
    }
}
